import { useMemo, useState } from "react";
import {
  FIGURINHA_DESCRICAO_MAX,
  FIGURINHA_NOME_MAX,
  type FigurinhaDaBiblioteca,
} from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import { dolar } from "../../lib/formato.js";
import { useAuth } from "../auth/useAuth.js";
import { Sobreposicao } from "../../components/Sobreposicao.js";
import { ImagemAmpliavel } from "../../components/Ampliar.js";
import { Area, Botao, Campo, Chave, Entrada, Falha, Selo } from "../../components/ui.js";
import { Aviso } from "./Aviso.js";
import { CabecalhoDoPainel } from "./CabecalhoDoPainel.js";
import { VisorDaFigurinha } from "./VisorDaFigurinha.js";
import {
  ROTULO_DA_ORIGEM,
  contadorDeLetras,
  dataDaImagem,
  dimensoesLegiveis,
  pesoLegivel,
} from "./apresentacao.js";
import { pedidoDeEdicao, rascunhoDe, type Rascunho } from "./edicao.js";
import { mensagemDeErro } from "./erros.js";
import { ROTULO_DO_TIPO } from "./tipos.js";

/**
 * O detalhe de uma imagem da biblioteca: ela grande no xadrez (toque para ampliar com zoom), o nome e a
 * descrição para editar, os dados do arquivo e, para o administrador, quem é o dono, o custo e o
 * interruptor "Pública".
 *
 * Abre com a imagem que a lista (ou o envio) já tinha, e consulta o servidor por cima: as URLs que vieram
 * na lista são assinadas e vencem, e a consulta traz as de agora — além de não depender de a imagem estar
 * na lista filtrada (a que acaba de ser enviada pode ser de um tipo que a aba aberta não mostra).
 *
 * Imagem pública de outra pessoa (`podeEditar` falso) é só para ver: ela é da casa, e quem a usa não a
 * edita nem a apaga. O servidor recusa de qualquer jeito; esconder os botões evita o convite ao erro.
 */
export function DetalheDaFigurinha({
  inicial,
  aoFechar,
  aoApagada,
}: {
  inicial: FigurinhaDaBiblioteca;
  aoFechar: () => void;
  /** Recebe o nome da imagem apagada, para a lista dizer o que aconteceu. */
  aoApagada: (nome: string) => void;
}) {
  const utils = trpc.useUtils();
  const { ehAdmin } = useAuth();

  const consulta = trpc.figurinhas.get.useQuery({ id: inicial.id }, { placeholderData: inicial });
  const item = consulta.data ?? inicial;

  const [rascunho, setRascunho] = useState<Rascunho>(() => rascunhoDe(inicial));
  // Dois lugares para o erro, cada um colado no botão que o causou: no celular o "Salvar" (no alto) e o
  // "Apagar" (no pé) ficam a uma rolagem de distância, e a frase longe do dedo passaria despercebida.
  const [erroAoSalvar, setErroAoSalvar] = useState<string | null>(null);
  const [erroDaAcao, setErroDaAcao] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);
  const [confirmandoApagar, setConfirmandoApagar] = useState(false);
  const [confirmandoDescarte, setConfirmandoDescarte] = useState(false);
  const [ampliando, setAmpliando] = useState(false);

  const pedido = useMemo(() => pedidoDeEdicao(item, rascunho), [item, rascunho]);
  const sujo = pedido.tipo !== "nada";

  const aposMudar = (novo: FigurinhaDaBiblioteca) => {
    // O que a tela guarda (e o que a próxima abertura mostra) é a resposta do servidor, não o que se digitou.
    utils.figurinhas.get.setData({ id: novo.id }, novo);
    void utils.figurinhas.list.invalidate();
  };

  const salvar = trpc.figurinhas.atualizar.useMutation({
    onSuccess: (novo) => {
      aposMudar(novo);
      setSalvo(true);
    },
    onError: (e) => setErroAoSalvar(mensagemDeErro(e, "Não foi possível salvar.")),
  });
  // Outra mutação, e não a mesma: o interruptor tem o estado de espera dele, separado do botão "Salvar".
  const publicar = trpc.figurinhas.atualizar.useMutation({
    onSuccess: (novo) => {
      setErroDaAcao(null);
      aposMudar(novo);
    },
    onError: (e) => setErroDaAcao(mensagemDeErro(e, "Não foi possível mudar a visibilidade.")),
  });
  const apagar = trpc.figurinhas.apagar.useMutation({
    onSuccess: () => {
      void utils.figurinhas.list.invalidate();
      aoApagada(item.nome);
    },
    onError: (e) => {
      setConfirmandoApagar(false);
      setErroDaAcao(mensagemDeErro(e, "Não foi possível apagar a imagem."));
    },
  });

  function editar(mudanca: Partial<Rascunho>) {
    setRascunho((r) => ({ ...r, ...mudanca }));
    setSalvo(false);
    setErroAoSalvar(null);
    setConfirmandoDescarte(false);
  }

  function pedirParaFechar() {
    // Um clique sem querer no "Fechar" (ou o Esc) não pode levar embora uma descrição escrita à mão.
    if (sujo && !confirmandoDescarte) {
      setConfirmandoDescarte(true);
      return;
    }
    aoFechar();
  }

  const editavel = item.podeEditar;
  const erroDe = (campo: "nome" | "descricao") =>
    pedido.tipo === "erro" && pedido.campo === campo ? pedido.mensagem : null;

  return (
    <>
      <Sobreposicao rotulo={`Imagem ${item.nome}`} aoFechar={pedirParaFechar}>
        <CabecalhoDoPainel
          titulo={item.nome}
          selos={
            <>
              <Selo tom="quieto">{ROTULO_DO_TIPO[item.tipo]}</Selo>
              {item.publica && <Selo tom="fixado">Pública</Selo>}
            </>
          }
          aoFechar={pedirParaFechar}
        />

        {/*
          No celular tudo numa coluna, na ordem de quem edita: a imagem, o formulário, os dados, e por
          último o que é do administrador e o "Apagar". Dos `sm` em diante são duas colunas — imagem e dados
          à esquerda; formulário e ações à direita, onde o dedo do administrador já está. Os dois blocos
          são `contents` no celular para a ordem do celular (`order-*`) valer entre os quatro filhos.
        */}
        <div className="flex flex-col gap-5 px-4 py-5 sm:grid sm:grid-cols-2 sm:items-start sm:px-6">
          <div className="contents sm:flex sm:flex-col sm:gap-5">
            <div className="order-1">
              <ImagemAmpliavel
                rotulo={`Ampliar ${item.nome}`}
                aoAmpliar={() => setAmpliando(true)}
                className="w-full!"
              >
                <span className="fundo-xadrez flex h-64 w-full items-center justify-center overflow-hidden rounded-peca border border-borda sm:h-72">
                  <img
                    src={item.urls.tela}
                    alt={item.nome}
                    width={item.largura}
                    height={item.altura}
                    draggable={false}
                    className="max-h-full max-w-full object-contain p-3"
                  />
                </span>
              </ImagemAmpliavel>
            </div>

            <dl className="order-3 grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-[0.875rem]">
              <Dado rotulo="Tipo">{ROTULO_DO_TIPO[item.tipo]}</Dado>
              <Dado rotulo="Tamanho">{dimensoesLegiveis(item.largura, item.altura)}</Dado>
              <Dado rotulo="Peso">{pesoLegivel(item.fileSize)}</Dado>
              <Dado rotulo="Origem">{ROTULO_DA_ORIGEM[item.origem]}</Dado>
              <Dado rotulo={item.origem === "ia" ? "Criada em" : "Enviada em"}>
                {dataDaImagem(item.createdAt)}
              </Dado>
              {item.referencias.length > 0 && (
                <Dado rotulo="Referências">
                  {item.referencias.length === 1
                    ? "1 imagem da biblioteca"
                    : `${item.referencias.length} imagens da biblioteca`}
                </Dado>
              )}
              {/* O que só o admin vê: o servidor manda nulo aos demais, e a tela nem desenha a linha. */}
              {ehAdmin && <Dado rotulo="Dono">{item.donoNome ?? "—"}</Dado>}
              {ehAdmin && item.origem === "ia" && (
                <>
                  <Dado rotulo="Custo">{item.custoUsd === null ? "—" : dolar(item.custoUsd)}</Dado>
                  <Dado rotulo="Modelo">{item.modelo ?? "—"}</Dado>
                </>
              )}
            </dl>
          </div>

          <div className="contents sm:flex sm:flex-col sm:gap-5">
            <form
              className="order-2 flex flex-col gap-4"
              onSubmit={(e) => {
                e.preventDefault();
                if (pedido.tipo === "salvar") salvar.mutate({ id: item.id, ...pedido.campos });
              }}
            >
              {consulta.isError && <Falha mensagem={mensagemDeErro(consulta.error)} />}

              {!editavel && (
                <Aviso tom="informacao">
                  Esta imagem é da casa: você pode usá-la, mas não editar.
                </Aviso>
              )}

              {editavel ? (
                <>
                  <Campo rotulo="Nome" erro={erroDe("nome")}>
                    <Entrada
                      value={rascunho.nome}
                      maxLength={FIGURINHA_NOME_MAX}
                      autoComplete="off"
                      onChange={(e) => editar({ nome: e.target.value })}
                    />
                  </Campo>
                  <Campo
                    rotulo="Descrição — é o que a IA lê para recriar o estilo"
                    erro={erroDe("descricao")}
                    dica={`${contadorDeLetras(rascunho.descricao, FIGURINHA_DESCRICAO_MAX)} letras. Apague o texto para remover a descrição.`}
                  >
                    <Area
                      value={rascunho.descricao}
                      placeholder="Sem descrição. Escreva o que a imagem mostra: formas, cores, estilo do traço."
                      onChange={(e) => editar({ descricao: e.target.value })}
                    />
                  </Campo>
                  <div className="flex flex-wrap items-center gap-3">
                    <Botao
                      type="submit"
                      tom="brasa"
                      disabled={pedido.tipo !== "salvar"}
                      carregando={salvar.isPending}
                    >
                      Salvar
                    </Botao>
                    <p role="status" className="text-[0.875rem] text-texto-suave">
                      {salvo ? "Salvo." : sujo ? "Há alterações ainda não salvas." : ""}
                    </p>
                  </div>
                </>
              ) : (
                <div>
                  <p className="mb-1.5 text-[0.8125rem] font-medium text-texto-suave">Descrição</p>
                  <p className="text-[0.9375rem] leading-relaxed whitespace-pre-line text-texto wrap-anywhere">
                    {item.descricao ?? "Esta imagem não tem descrição."}
                  </p>
                </div>
              )}

              {erroAoSalvar && <Falha mensagem={erroAoSalvar} />}

              {confirmandoDescarte && (
                <Aviso tom="informacao">
                  <p>Você mudou o nome ou a descrição e não salvou.</p>
                  <div className="mt-2 flex flex-wrap gap-2">
                    <Botao type="button" tom="quieto" onClick={aoFechar}>
                      Fechar sem salvar
                    </Botao>
                    <Botao
                      type="button"
                      tom="fantasma"
                      onClick={() => setConfirmandoDescarte(false)}
                    >
                      Continuar editando
                    </Botao>
                  </div>
                </Aviso>
              )}
            </form>

            {(ehAdmin || editavel) && (
              <div className="order-4 flex flex-col gap-5">
                {ehAdmin && (
                  <div className="rounded-peca border border-borda bg-superficie px-4 py-3">
                    <Chave
                      ligada={item.publica}
                      aoAlternar={(publica) => publicar.mutate({ id: item.id, publica })}
                      rotulo="Imagem pública"
                      texto="Pública"
                      descricaoId="explicacao-publica"
                      disabled={publicar.isPending || apagar.isPending}
                    />
                    <p
                      id="explicacao-publica"
                      className="text-[0.8125rem] leading-snug text-texto-fraco"
                    >
                      {item.publica
                        ? "Todas as casas veem e usam esta imagem nas folhas. Só o dono e o administrador a editam."
                        : "Só o dono (e os operadores dele) vê esta imagem. Torne-a pública para todas as casas."}
                    </p>
                  </div>
                )}

                {erroDaAcao && <Falha mensagem={erroDaAcao} />}

                {editavel &&
                  (confirmandoApagar ? (
                    <Aviso tom="perigo">
                      <p>
                        Apagar <strong className="font-medium">{item.nome}</strong>? Isso apaga
                        também a descrição, o arquivo e as versões reduzidas, e não tem volta. Uma
                        folha que ainda use esta imagem terá de trocá-la por outra.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        <Botao
                          type="button"
                          tom="descartar"
                          carregando={apagar.isPending}
                          onClick={() => apagar.mutate({ id: item.id })}
                        >
                          Apagar mesmo assim
                        </Botao>
                        <Botao
                          type="button"
                          tom="fantasma"
                          autoFocus
                          disabled={apagar.isPending}
                          onClick={() => setConfirmandoApagar(false)}
                        >
                          Manter a imagem
                        </Botao>
                      </div>
                    </Aviso>
                  ) : (
                    <div>
                      <Botao
                        type="button"
                        tom="descartar"
                        disabled={salvar.isPending || publicar.isPending}
                        onClick={() => setConfirmandoApagar(true)}
                      >
                        Apagar imagem
                      </Botao>
                    </div>
                  ))}
              </div>
            )}
          </div>
        </div>
      </Sobreposicao>
      {/* Irmão do painel, e não filho: o visor é outra camada, e o que se monta dentro de um painel herda os
        eventos dele pela árvore do React. */}
      {ampliando && <VisorDaFigurinha item={item} aoFechar={() => setAmpliando(false)} />}
    </>
  );
}

function Dado({ rotulo, children }: { rotulo: string; children: React.ReactNode }) {
  return (
    <>
      <dt className="text-texto-fraco">{rotulo}</dt>
      <dd className="min-w-0 text-texto wrap-anywhere">{children}</dd>
    </>
  );
}
